import { useState } from 'react'

import DotsHorizontalIcon from '@heroicons/react/outline/DotsHorizontalIcon'
import PencilIcon from '@heroicons/react/outline/PencilIcon'
import ShareIcon from '@heroicons/react/outline/ShareIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import { useDecision } from '@optimizely/react-sdk'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { NetworkVisibility, Member } from '@tribeplatform/gql-client/types'
import { useAuthToken, useSpaceUtil } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { FeaturesList } from '../../lib/optimizely/feature.list'
import { AuditLogsModal } from '../AuditLog/AuditLogsModal'
import { ProfileSettingsModal } from '../Settings/ProfileSettingsModal'
import { ShareModal } from '../ShareModal'

export const MemberActionButtons = ({ member }: { member: Member }) => {
  const [shareModal, setShareModal] = useState<boolean>(false)
  const [editModal, setEditModal] = useState(false)
  const [auditLogsModal, setAuditLogsModal] = useState<boolean>(false)

  const [decisionAuditLogs] = useDecision(FeaturesList.AuditLogs)

  const { generateMemberPath } = useSpaceUtil()
  const {
    data: { network },
  } = useAuthToken()
  const [canUpdateMember] = hasScopesPermission(member, [
    'updateMember',
    'updateNetwork',
  ])
  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])

  const memberUrl = `https://${network?.domain}${generateMemberPath(member)}`

  return (
    <>
      <Dropdown placement="bottom-end">
        <Dropdown.ButtonMinimal>
          <Button variant="outline" leadingIcon={<DotsHorizontalIcon />} />
        </Dropdown.ButtonMinimal>
        <Dropdown.Items className="w-56">
          <Dropdown.Item
            leadingIcon={<ShareIcon />}
            onClick={() => {
              setShareModal(true)
            }}
          >
            Share
          </Dropdown.Item>
          {decisionAuditLogs?.enabled && canUpdateNetwork && (
            <Dropdown.Item
              leadingIcon={<ShieldExclamationIcon />}
              onClick={() => setAuditLogsModal(true)}
            >
              Audit logs
            </Dropdown.Item>
          )}
        </Dropdown.Items>
      </Dropdown>
      {canUpdateMember ? (
        <Button
          onClick={() => setEditModal(true)}
          variant="outline"
          className="flex-shrink-0"
          leadingIcon={<PencilIcon />}
        >
          Edit profile
        </Button>
      ) : null}

      <ShareModal
        isPrivate={network.visibility === NetworkVisibility.PRIVATE}
        memberId={member?.id}
        open={shareModal}
        onClose={() => setShareModal(false)}
        title={member?.name}
        url={memberUrl}
      />
      <ProfileSettingsModal
        open={editModal}
        onClose={() => {
          setEditModal(false)
        }}
        member={member}
      />
      {auditLogsModal && (
        <AuditLogsModal
          open
          onClose={() => setAuditLogsModal(false)}
          actorId={member?.id}
        />
      )}
    </>
  )
}
